import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useLanguage } from "../App";
import { Button } from "./ui/button";
import {
  Trophy, RefreshCw, Wallet, Target, Users, TrendingUp, ChevronLeft, ChevronRight,
  Award, Loader2, Eye, EyeOff, Download, Share2
} from "lucide-react";
import { toast } from "sonner";

const MEDALS = ["", "bg-amber-400", "bg-slate-400", "bg-amber-700"];

const SalesRankingDashboard = () => {
  const { language } = useLanguage();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publicMode, setPublicMode] = useState(false);

  const fmt = (v) => `€${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0 })}`;

  const load = async (m = month, y = year) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales/ranking`, {
        params: { month: m, year: y },
        withCredentials: true,
      });
      setRanking(res.data.ranking || []);
    } catch {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
    load(m, y);
  };

  const periodLabel = () => {
    const monthNames = [
      "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
      "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  const totals = ranking.reduce(
    (acc, r) => ({
      leads_created: acc.leads_created + (r.leads_created || 0),
      deals_closed: acc.deals_closed + (r.deals_closed || 0),
      revenue: acc.revenue + (r.revenue_generated || 0),
      commissions: acc.commissions + (r.commissions_earned || 0),
    }),
    { leads_created: 0, deals_closed: 0, revenue: 0, commissions: 0 }
  );

  const copyShareLink = () => {
    const url = window.location.origin + "/admin/ranking";
    navigator.clipboard.writeText(url);
    toast.success(language === "it" ? "Link copiato" : "Link copied");
  };

  return (
    <div className="space-y-6" data-testid="admin-ranking-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" />
            {language === "it" ? "Classifica Sales" : "Sales Ranking"}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">{periodLabel()}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" size="sm"
            onClick={() => shiftMonth(-1)}
            className="rounded-full h-9 w-9 p-0"
            data-testid="ranking-prev-month"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium min-w-[120px] text-center">{periodLabel()}</span>
          <Button
            variant="outline" size="sm"
            onClick={() => shiftMonth(1)}
            className="rounded-full h-9 w-9 p-0"
            data-testid="ranking-next-month"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => load()}
            className="rounded-full"
            data-testid="ranking-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {language === "it" ? "Aggiorna" : "Refresh"}
          </Button>
          <Button
            variant={publicMode ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setPublicMode(!publicMode);
              if (!publicMode) toast.success(language === "it" ? "Modalità pubblica: valori anonimizzati" : "Public mode: values anonymized");
            }}
            className="rounded-full"
            data-testid="ranking-toggle-public"
          >
            {publicMode ? <Eye className="w-4 h-4 mr-1.5" /> : <EyeOff className="w-4 h-4 mr-1.5" />}
            {language === "it" ? (publicMode ? "Vista pubblica ON" : "Vista pubblica OFF") : (publicMode ? "Public view ON" : "Public view OFF")}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={copyShareLink}
            className="rounded-full"
            data-testid="ranking-share-btn"
          >
            <Share2 className="w-4 h-4 mr-1.5" />
            {language === "it" ? "Condividi" : "Share"}
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ranking-totals">
        <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
          <Users className="w-5 h-5 text-blue-500 mb-1" />
          <p className="text-xs text-blue-600 dark:text-blue-400">{language === "it" ? "Lead creati" : "Leads"}</p>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{totals.leads_created}</p>
        </div>
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
          <Target className="w-5 h-5 text-emerald-500 mb-1" />
          <p className="text-xs text-emerald-600 dark:text-emerald-400">{language === "it" ? "Deal chiusi" : "Closed"}</p>
          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{totals.deals_closed}</p>
        </div>
        <div className="p-4 rounded-2xl bg-cyan-50 dark:bg-cyan-950/20 border border-cyan-200 dark:border-cyan-800">
          <TrendingUp className="w-5 h-5 text-cyan-500 mb-1" />
          <p className="text-xs text-cyan-600 dark:text-cyan-400">{language === "it" ? "Fatturato" : "Revenue"}</p>
          <p className="text-2xl font-bold text-cyan-700 dark:text-cyan-300">{fmt(totals.revenue)}</p>
        </div>
        <div className="p-4 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white">
          <Wallet className="w-5 h-5 opacity-80 mb-1" />
          <p className="text-xs text-violet-100">{language === "it" ? "Commissioni totali" : "Total commissions"}</p>
          <p className="text-2xl font-bold">{fmt(totals.commissions)}</p>
        </div>
      </div>

      {/* Ranking list */}
      <div className="space-y-3" data-testid="ranking-list">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
        ) : ranking.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-border bg-muted/30" data-testid="ranking-empty">
            <Trophy className="w-12 h-12 mx-auto text-muted-foreground opacity-40 mb-3" />
            <p className="font-medium text-sm">{language === "it" ? "Nessun dato per questo periodo" : "No data for this period"}</p>
          </div>
        ) : (
          ranking.map((r, idx) => {
            const isTop = r.rank <= 3;
            const displayName = publicMode
              ? `Sales #${r.rank}`
              : r.name;
            return (
              <div
                key={r.sales_id || idx}
                className={`flex items-center gap-4 p-4 rounded-2xl border transition-all hover:shadow-md ${
                  isTop ? "bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200 dark:border-amber-800" : "bg-card"
                }`}
                data-testid={`ranking-row-${r.rank}`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-base flex-shrink-0 ${
                  r.rank <= 3 ? `${MEDALS[r.rank]} text-white shadow-lg` : "bg-muted text-muted-foreground"
                }`}>
                  {r.rank <= 3 ? <Trophy className="w-5 h-5" /> : `#${r.rank}`}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm truncate">{displayName}</p>
                    {r.rank === 1 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold">TOP</span>}
                  </div>
                  {!publicMode && (
                    <p className="text-xs text-muted-foreground truncate">{r.email || ""}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                    <span>{r.leads_created} lead</span>
                    <span>•</span>
                    <span>{r.leads_contacted} {language === "it" ? "contattati" : "contacted"}</span>
                    <span>•</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">{r.deals_closed} {language === "it" ? "chiusi" : "closed"}</span>
                    <span>•</span>
                    <span>{r.closing_rate || 0}% closing</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{fmt(r.revenue_generated)}</p>
                  <p className="text-[11px] text-muted-foreground flex items-center justify-end gap-1">
                    <Award className="w-3 h-3" />
                    {fmt(r.commissions_earned)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SalesRankingDashboard;
